/* Off Brand Humor — minimal storefront behaviour.
   Only the vertical wards carousel needs scripting; everything else is CSS. */
(() => {
  const VISIBLE_DEPTH = 2;

  class WardsCarousel {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll('[data-obh-wards-item]'));
      if (this.items.length === 0) return;

      this.index = Math.floor(this.items.length / 2);
      this.prevBtn = root.querySelector('[data-obh-wards-prev]');
      this.nextBtn = root.querySelector('[data-obh-wards-next]');
      this.viewport = root.querySelector('.wards__viewport');

      this.prevBtn?.addEventListener('click', () => this.go(-1));
      this.nextBtn?.addEventListener('click', () => this.go(1));

      this.items.forEach((item, i) => {
        item.addEventListener('click', (event) => {
          if (i === this.index) return;
          event.preventDefault();
          this.select(i);
        });
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.go(-1);
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.go(1);
        }
      });

      this.bindTouch();
      this.render();
    }

    bindTouch() {
      if (!this.viewport) return;
      let startY = null;

      this.viewport.addEventListener(
        'touchstart',
        (event) => {
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.viewport.addEventListener(
        'touchend',
        (event) => {
          if (startY === null) return;
          const delta = event.changedTouches[0].clientY - startY;
          if (Math.abs(delta) > 40) this.go(delta < 0 ? 1 : -1);
          startY = null;
        },
        { passive: true }
      );
    }

    go(step) {
      this.select(this.index + step);
    }

    select(index) {
      const count = this.items.length;
      this.index = ((index % count) + count) % count;
      this.render();
    }

    render() {
      const count = this.items.length;
      const half = Math.floor(count / 2);

      this.items.forEach((item, i) => {
        let rel = i - this.index;
        if (rel > half) rel -= count;
        if (rel < -half) rel += count;

        const hidden = Math.abs(rel) > VISIBLE_DEPTH;
        item.style.setProperty('--rel', String(rel));
        item.dataset.rel = String(rel);
        item.dataset.hidden = hidden ? 'true' : 'false';
        item.setAttribute('aria-hidden', hidden ? 'true' : 'false');

        const link = item.querySelector('a');
        if (link) link.tabIndex = hidden ? -1 : 0;
      });
    }
  }


  /* Horizontal product row: native scroll-snap, arrows just nudge it by one card. */
  class Row {
    constructor(root) {
      this.track = root.querySelector('[data-obh-row-track]');
      this.prev = root.querySelector('[data-obh-row-prev]');
      this.next = root.querySelector('[data-obh-row-next]');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      window.addEventListener('resize', () => this.sync(), { passive: true });
      this.sync();
    }

    step() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scroll(dir) {
      this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 1;
      if (this.next) this.next.disabled = x >= max - 1;
    }
  }


  /* Product detail: gallery, variant matching, quantity. */
  class ProductDetail {
    constructor(root) {
      this.root = root;
      this.variants = this.readVariants();
      this.idInput = root.querySelector('[data-obh-variant-id]');
      this.addBtn = root.querySelector('[data-obh-add]');
      this.addText = root.querySelector('[data-obh-add-text]');
      this.priceBox = root.querySelector('[data-obh-price]');
      this.soldOutLabel = this.addBtn ? this.addBtn.dataset.soldOut : null;

      root.querySelectorAll('[data-obh-thumb]').forEach((btn) => {
        btn.addEventListener('click', () => this.showMedia(btn.dataset.obhThumb));
      });

      root.querySelectorAll('[data-obh-option-value]').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });

    }

    readVariants() {
      const tag = this.root.querySelector('[data-obh-variants]');
      if (!tag) return [];
      try {
        return JSON.parse(tag.textContent);
      } catch {
        return [];
      }
    }

    showMedia(id) {
      this.root.querySelectorAll('[data-obh-slide]').forEach((slide) => {
        const active = slide.dataset.obhSlide === String(id);
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.root.querySelectorAll('[data-obh-thumb]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.obhThumb === String(id));
      });
    }

    selectedOptions() {
      return [...this.root.querySelectorAll('[data-obh-option-value]:checked')].map((i) => i.value);
    }

    onOptionChange() {
      const chosen = this.selectedOptions();
      const variant = this.variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
      this.apply(variant);
    }

    apply(variant) {
      if (this.idInput) this.idInput.value = variant ? variant.id : '';

      if (this.addBtn) {
        const buyable = Boolean(variant && variant.available);
        this.addBtn.disabled = !buyable;
        if (this.addText && !buyable) {
          this.addText.textContent = variant ? this.addBtn.dataset.soldOutText : this.addBtn.dataset.unavailableText;
        } else if (this.addText) {
          this.addText.textContent = this.addBtn.dataset.addText;
        }
      }

      if (variant && this.priceBox) {
        const current = this.priceBox.querySelector('.pdp__price-current');
        const was = this.priceBox.querySelector('.pdp__price-was');
        if (current) current.textContent = this.money(variant.price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.priceBox.classList.toggle('pdp__price--sale', Boolean(onSale));
        if (was) {
          was.textContent = onSale ? this.money(variant.compare_at_price) : '';
          was.hidden = !onSale;
        }
      }

      const sku = this.root.querySelector('[data-obh-sku]');
      if (sku && variant) sku.textContent = variant.sku || '';

      if (variant && variant.featured_media) this.showMedia(variant.featured_media.id);

      if (variant && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    money(cents) {
      const format = this.root.dataset.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

  }

  /* Quantity stepper, shared by the product page and the cart. */
  const bindStepper = (btn) => {
    btn.addEventListener('click', () => {
      const input = btn.parentElement.querySelector('[data-obh-qty-input]');
      if (!input) return;
      const min = Number(input.min || 1);
      const next = Math.max(min, (parseInt(input.value, 10) || min) + Number(btn.dataset.obhQty));
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll('[data-obh-wards]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new WardsCarousel(root));
    });

    scope.querySelectorAll('[data-obh-row]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new Row(root));
    });

    scope.querySelectorAll('[data-obh-product]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new ProductDetail(root));
    });

    scope.querySelectorAll('[data-obh-qty]').forEach((btn) => {
      if (instances.has(btn)) return;
      instances.set(btn, true);
      bindStepper(btn);
    });

    scope.querySelectorAll('[data-obh-autosubmit]').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, true);
      el.addEventListener('change', () => el.form && el.form.submit());
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('[data-obh-wards-item]');
    const root = item?.closest('[data-obh-wards]');
    const carousel = root && instances.get(root);
    if (carousel) carousel.select(Number(item.dataset.index));
  });
})();
